import { getInitials } from './equipe2Helpers';
import {
  EMPTY_EQUIPE2_TECH_FILTERS,
  filterTecnicosList,
  hasEquipe2TechLevelFilter,
} from './equipe2TechFilterHelpers';

export { EMPTY_EQUIPE2_TECH_FILTERS as EMPTY_HIERARCHY_FILTERS };

export const HIERARCHY_COORD_COLORS = [
  { soft: '#EEEAFF', strong: '#5B3DE0' },
  { soft: '#E8F0FE', strong: '#1D4FBF' },
  { soft: '#DFF5F3', strong: '#0B7A7A' },
  { soft: '#FCEAE5', strong: '#B94F3A' },
];

function matchesName(name, search) {
  return !!search && String(name || '').toLowerCase().includes(search);
}

function buildTechAvatar(tech, colorSoft, colorStrong, search) {
  const matched = matchesName(tech.name, search);
  const dim = !!search && !matched;
  return {
    id: tech.id,
    name: tech.name,
    initials: getInitials(tech.name),
    matched,
    dim,
    colorSoft,
    colorStrong,
  };
}

function buildArea(area, techs, colorSoft, colorStrong, search) {
  const technicians = techs.map((t) => buildTechAvatar(t, colorSoft, colorStrong, search));
  const areaMatched = matchesName(area.name, search)
    || technicians.some((t) => matchesName(t.name, search));
  const areaDim = !!search && !areaMatched;

  return {
    id: area.id,
    name: area.name,
    abbr: area.abbr || '—',
    count: technicians.length,
    hasNoTechs: technicians.length === 0,
    technicians,
    areaMatched,
    areaDim,
    colorSoft,
    colorStrong,
  };
}

export function createHierarchyFilterSuggestionsFetcher({ coordenadores, areas, tecnicos, gestaoByCodigo }) {
  return async (fieldKey, searchTerm) => {
    const term = String(searchTerm || '').trim().toLowerCase();
    const match = (name) => !term || String(name || '').toLowerCase().includes(term);

    if (fieldKey === 'coordenador') {
      return coordenadores.map((c) => c.nome).filter(match).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }
    if (fieldKey === 'area') {
      return areas.map((a) => a.nome).filter(match).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }
    if (fieldKey === 'tecnico') {
      return tecnicos.map((t) => t.nome).filter(match).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }
    if (fieldKey === 'cargo' && gestaoByCodigo) {
      const set = new Set();
      for (const t of tecnicos) {
        const g = gestaoByCodigo.get(String(t.id));
        const c = g?.cargo && String(g.cargo).trim();
        set.add(c || 'Sem cargo');
      }
      return [...set].filter(match).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }
    return [];
  };
}

export function buildHierarchyModel({
  coordenadores,
  areas,
  tecnicos,
  search = '',
  filters = EMPTY_EQUIPE2_TECH_FILTERS,
  gestaoByCodigo = null,
  espLinks = [],
}) {
  const hSearch = search.trim().toLowerCase();
  const selCoords = filters.coordenadores || [];
  const selAreas = filters.areas || [];
  const hasCoordFilter = selCoords.length > 0;
  const hasAreaFilter = selAreas.length > 0;
  const hasTechLevelFilter = hasEquipe2TechLevelFilter(filters);
  const gestaoMap = gestaoByCodigo || new Map();

  const coordSource = hasCoordFilter
    ? coordenadores.filter((c) => selCoords.includes(c.nome))
    : coordenadores;

  const getTechsByArea = (areaId) => {
    const list = filterTecnicosList(
      tecnicos.filter((t) => t.areaId === areaId),
      filters,
      gestaoMap,
      espLinks,
    );
    return list.map((t) => ({ id: t.id, name: t.nome }));
  };

  const hierarchyCoordinators = coordSource.map((coord, idx) => {
    const color = HIERARCHY_COORD_COLORS[idx % HIERARCHY_COORD_COLORS.length];
    let coordAreas = areas.filter((a) => a.coordenadorId === coord.id);
    if (hasAreaFilter) {
      coordAreas = coordAreas.filter((a) => selAreas.includes(a.nome));
    }
    let builtAreas = coordAreas.map((a) =>
      buildArea(
        { id: a.id, name: a.nome, abbr: a.abreviacao },
        getTechsByArea(a.id),
        color.soft,
        color.strong,
        hSearch,
      ),
    );
    if (hasTechLevelFilter) {
      builtAreas = builtAreas.filter((a) => a.count > 0);
    }
    const techCount = builtAreas.reduce((sum, a) => sum + a.count, 0);
    const coordMatched = matchesName(coord.nome, hSearch)
      || builtAreas.some((a) => a.areaMatched || a.technicians.some((t) => matchesName(t.name, hSearch)));
    const coordDim = !!hSearch && !coordMatched;

    return {
      id: coord.id,
      name: coord.nome,
      colorSoft: color.soft,
      colorStrong: color.strong,
      areaCount: builtAreas.length,
      techCount,
      areas: builtAreas,
      coordDim,
    };
  });

  let unassignedRaw = areas.filter((a) => a.coordenadorId === null);
  if (hasAreaFilter) {
    unassignedRaw = unassignedRaw.filter((a) => selAreas.includes(a.nome));
  }
  let hierarchyUnassignedAreas = unassignedRaw.map((a) =>
    buildArea(
      { id: a.id, name: a.nome, abbr: a.abreviacao },
      getTechsByArea(a.id),
      '#F0EEF7',
      '#8b849a',
      hSearch,
    ),
  );
  if (hasTechLevelFilter) {
    hierarchyUnassignedAreas = hierarchyUnassignedAreas.filter((a) => a.count > 0);
  }

  const visibleCoords = hierarchyCoordinators.filter((coord) => {
    if (!hasAreaFilter && !hasTechLevelFilter) return true;
    return coord.areas.length > 0;
  });

  const countVisibleTechs = (areaList) =>
    areaList.reduce((sum, area) => sum + area.technicians.length, 0);

  const visibleAreaCount = visibleCoords.reduce((sum, c) => sum + c.areas.length, 0)
    + hierarchyUnassignedAreas.length;
  const visibleTechCount = countVisibleTechs(visibleCoords.flatMap((c) => c.areas))
    + countVisibleTechs(hierarchyUnassignedAreas);

  return {
    coordinators: visibleCoords,
    unassignedAreas: hierarchyUnassignedAreas,
    hasUnassigned: hierarchyUnassignedAreas.length > 0,
    stats: {
      coordCount: visibleCoords.length,
      areaCount: visibleAreaCount,
      techCount: visibleTechCount,
    },
  };
}
